import styled from "styled-components"
import { useState } from "react"
import useTranslation from '../../hooks/useTranslation'

import { LinkText } from "../generic/Core"
import IconButton from "../generic/IconButton"
import { Icon_Edit, Icon_Plus } from "../../styles/icons"
import { FlexBox } from "../generic/Layout"

import DisplayColumns from "./DisplayColumns"
import { useStore } from "../pageView/Page"
import Alert from '../toast/Alert'
import resolve from "../../../../common/utils/object/resolve"
import validateWarning from "../../utils/display/validateWarning"

/*
 * `$divided` puts a hairline above every line row but the first.
 *
 * A lines display stacks one of these per row, and with nothing between them a
 * three-legged journal read as one block of text — the reader had to work out
 * where one account ended and the next began. A border rather than a gap
 * because the rows are a table in all but name, and it is drawn on the row
 * itself so a section of one line never shows a stray rule.
 */
const DisplayWrapper = styled.div`
    position: relative;
    ${({ $divided, theme }) => $divided && `
        border-top: 1px solid ${theme.table.border};
        padding-top: 0.6rem;
        margin-top: 0.6rem;
    `}
`

const IconButtonsContainer = styled.div`
    position: absolute;
    right: ${(props) => props.$right ? props.$right : '10%'};
    top: calc(50% - 1rem);
`

const AlertWrapper = styled.div`
    position: absolute;
    top: 0.5rem;
    right: 6rem;
    width: 1.25rem;
    padding: 0;
    margin: 0;
    color: #842029;
`

const resolveData = (s, type, path, linesArray, idx) => {
    if (type === 'root') return path.length > 0 ? resolve(path.join('.'), s) : s
    if (type === 'lines') return (resolve(linesArray, s) || [])[idx] ?? null
    return null
}

/*
 * `divided: false` is for a caller that draws its own rule between rows —
 * LineElement puts a Separator under every card line, and the hairline on top
 * of it drew a second line above each row but the first.
 */
const Display = ({ section, clickAction, type, idx, showAction = false, path = [], divided = true }) => {
    const [isHovered, setIsHovered] = useState(false)

    const { meta, displaySection: config } = section
    const linesArray = meta.linesArray

    const [currency] = useStore(s => s.currency)
    const [displayData] = useStore(s => resolveData(s, type, path, linesArray, idx))

    const isEmpty = !resolve(config.emptyField, displayData)
    const emptyMessage = config.emptyMessage || 'all.data.missing.message'
    const showWarning = validateWarning(meta.warning, displayData)

    const { t } = useTranslation()

    // A lines display renders once per row, so the row number has to be in the
    // id or every row would answer to the same one — the same shape the line's
    // own collapse uses.
    const editTestId = type === 'lines'
        ? `${section.id}.section.line.${idx + 1}.edit`
        : `${section.id}.edit`

    /*
     * The section itself, by the same rule its buttons already follow: the
     * config's `id`, row-numbered where a lines display repeats.
     *
     * A display had none, so nothing could assert what one shows — which is how
     * a journal came to display a raw 'PAYMENT' and a total labelled with the
     * wrong currency for months. There are no component tests here, so a test id
     * on the section is the only way a spec can read a display at all, and
     * reading the whole section is the right grain: a spec should assert that the
     * customer is on the line, not which of nine conditional elements drew it.
     */
    const sectionTestId = type === 'lines'
        ? `${section.id}.${idx + 1}`
        : section.id

    return (
        <DisplayWrapper
            data-testid={sectionTestId}
            $divided={divided && type === 'lines' && idx > 0}
            className="hover-container"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            { // 🧩 Conditional display — shows the actual DisplayColumns
                !isEmpty && (
                <DisplayColumns config={config} displayData={displayData} currency={currency} />
            )}

            { // 🧩 Conditional display — shows the edit button
                !isEmpty && config.showEditOnHover !== false && ((isHovered && !meta.readOnly) || showAction) && (
                <IconButtonsContainer $right={config.editButtonRightOffset}>
                    <IconButton icon={Icon_Edit} $rounded $variant='secondary' clickAction={clickAction} testId={editTestId} />
                </IconButtonsContainer>
            )}

            { // 🧩 Conditional display — shows the add button
                isEmpty && config.showEmptyPrompt !== false && (
                <FlexBox style={{ alignItems: 'center' }}>
                    <IconButton icon={Icon_Plus} $rounded $variant='primary' $size='2rem' clickAction={clickAction} testId={`${section.id}.add`} />
                    <LinkText onClick={clickAction}>{t(emptyMessage)}</LinkText>
                </FlexBox>
            )}

            { // 🧩 Conditional display — shows the warning icon
                showWarning && (
                <AlertWrapper>
                    <Alert.Icon type='warning' />
                </AlertWrapper>
            )}
        </DisplayWrapper>
    )
}

export default Display
